// ABOUTME: Displays formatted schedule date with variation code visual treatments (RMC only).
// Extracted from OrderDetailsHeader to reduce complexity and improve reusability.

import {dateFormatter, timeFormatter, Typography} from '@hconnect/uikit'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {TimezoneWarning} from '../../components'
import {VariationCodeBanner} from '../../components/VariationCodeBanner'
import {useVariationCode} from '../../hooks/useVariationCode'
import {Order} from '../../Order.types'
import {
  VARIATION_CODE_DANGER_COLOR,
  variationCodeCaptionStyle
} from '../../variationCode.utils'

interface ScheduleDateProps {
  order: Order
  userOffset: number
  eventLocationOffset?: number
}

export const ScheduleDate: React.FC<ScheduleDateProps> = ({
  order,
  userOffset,
  eventLocationOffset
}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation()

  const {
    showDateAsterisk,
    showDateDanger,
    showDateStrikethrough,
    showInlineBanner,
    adjustedVariationTime,
    showRequestedDateTimeWithStrikeThrough,
    variationDescription
  } = useVariationCode(order, eventLocationOffset)

  if (!order?.schedules?.length) {
    return null
  }

  const scheduleStartDate = order.schedules[0].scheduleStartDate
  if (!scheduleStartDate) {
    return (
      <Typography color="textPrimary" variant="subtitle1">
        {'-'}
      </Typography>
    )
  }

  const displayDate = scheduleStartDate

  return (
    <>
      <span
        style={{
          ...(showDateStrikethrough && !showRequestedDateTimeWithStrikeThrough
            ? {textDecoration: 'line-through'}
            : {})
        }}
      >
        {`${dateFormatter(
          displayDate,
          language,
          eventLocationOffset
        )}, `}
        {showRequestedDateTimeWithStrikeThrough ? (
          <>
            <span>
              {adjustedVariationTime}
              {showDateAsterisk ? ' *' : ''}
            </span>
            {' '}
            <span style={{textDecoration: 'line-through'}}>
              {timeFormatter(displayDate, language, eventLocationOffset)}
            </span>
          </>
        ) : (
          <span
            style={{
              ...(showDateDanger ? {color: VARIATION_CODE_DANGER_COLOR} : {})
            }}
          >
            {timeFormatter(
              displayDate,
              language,
              eventLocationOffset
            )}
            {showDateAsterisk ? ' *' : ''}
          </span>
        )}
      </span>
      <TimezoneWarning
        t={t}
        order={order}
        userOffset={userOffset}
        eventLocationOffset={eventLocationOffset}
      />
      {showDateAsterisk && variationDescription && !showDateStrikethrough && (
        <Typography
          variant="caption"
          style={variationCodeCaptionStyle}
          data-test-id="variation-code-description"
        >
          {'* '}{variationDescription}
        </Typography>
      )}
      {showInlineBanner && (
        <VariationCodeBanner order={order} placement="inline" />
      )}
    </>
  )
}
